import React from 'react';
import type { ProjectPhase } from '../../types';
import type { DraftTask } from '../../hooks/useProjectDraft';

/** ISO/`yyyy-mm-dd` → epoch ms (ou `null` se ausente/inválido). */
function toMs(d?: string | null): number | null {
  if (!d) return null;
  const t = new Date(`${d}T00:00:00`).getTime();
  return Number.isNaN(t) ? null : t;
}

/** hex (`#rrggbb`) → rgba com alpha. Fallback cinza. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return `rgba(148, 163, 184, ${alpha})`;
  return `rgba(${parseInt(m[1] as string, 16)}, ${parseInt(m[2] as string, 16)}, ${parseInt(m[3] as string, 16)}, ${alpha})`;
}

export interface DraftMiniGanttProps {
  phases: ProjectPhase[];
  tasks: DraftTask[];
}

/**
 * Preview do cronograma do rascunho — uma barra por fase.
 *
 * Diferente do `MiniGantt` da listagem (que recebe só o período agregado do
 * `ProjectListItem`), aqui temos as tarefas completas do rascunho, então
 * desenhamos **uma barra por fase** cobrindo o intervalo
 * `min(startDate)…max(endDate)` das tarefas daquela fase, na cor da fase, com
 * losangos nos marcos e a linha "hoje". Fases sem tarefas datadas são omitidas.
 */
export const DraftMiniGantt: React.FC<DraftMiniGanttProps> = ({ phases, tasks }) => {
  const dated = tasks.filter((t) => toMs(t.startDate) !== null && toMs(t.endDate) !== null);

  if (dated.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-md text-center px-3"
        style={{
          minHeight: 64,
          fontSize: 11,
          backgroundColor: 'rgba(148, 163, 184, 0.10)',
          color: 'var(--theme-muted-text)',
        }}
        data-testid="project-form-draft-gantt-empty"
      >
        Adicione tarefas com datas para ver o cronograma
      </div>
    );
  }

  const allStart = Math.min(...dated.map((t) => toMs(t.startDate) as number));
  const allEnd = Math.max(...dated.map((t) => toMs(t.endDate) as number));
  const span = Math.max(1, allEnd - allStart);
  const posOf = (ms: number): number => Math.max(0, Math.min(100, ((ms - allStart) / span) * 100));

  const nowMs = Date.now();
  const todayPos = nowMs >= allStart && nowMs <= allEnd ? posOf(nowMs) : null;

  // Fases que têm ao menos uma tarefa datada, na ordem definida.
  const rows = phases
    .map((phase) => {
      const phaseTasks = dated.filter((t) => t.phaseId === phase.id);
      if (phaseTasks.length === 0) return null;
      const start = Math.min(...phaseTasks.map((t) => toMs(t.startDate) as number));
      const end = Math.max(...phaseTasks.map((t) => toMs(t.endDate) as number));
      const milestones = phaseTasks
        .filter((t) => t.isMilestone)
        .map((t) => posOf(toMs(t.startDate) as number));
      return { phase, left: posOf(start), width: Math.max(2, posOf(end) - posOf(start)), milestones };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  return (
    <div className="space-y-2" data-testid="project-form-draft-gantt">
      {rows.map(({ phase, left, width, milestones }) => (
        <div key={phase.id} className="flex items-center gap-2">
          <div
            className="flex items-center gap-1.5"
            style={{ width: 76, flex: 'none' }}
          >
            <span
              className="inline-block rounded-full"
              style={{ width: 8, height: 8, backgroundColor: phase.color }}
            />
            <span
              className="flex-1 min-w-0 truncate"
              style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}
            >
              {phase.name}
            </span>
          </div>
          <div
            className="relative rounded flex-1"
            style={{
              height: 14,
              backgroundColor: 'rgba(148, 163, 184, 0.10)',
            }}
          >
            <div
              className="absolute top-0 bottom-0 rounded"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: hexToRgba(phase.color, 0.85),
              }}
            />
            {milestones.map((pos, i) => (
              <span
                key={i}
                className="absolute"
                style={{
                  left: `calc(${pos}% - 5px)`,
                  top: 2,
                  width: 10,
                  height: 10,
                  transform: 'rotate(45deg)',
                  borderRadius: 2,
                  backgroundColor: phase.color,
                  boxShadow: '0 0 0 2px var(--theme-card-background)',
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {todayPos !== null && (
        <div className="relative" style={{ height: 12 }}>
          <div
            className="absolute top-0 bottom-0"
            style={{ left: `${todayPos}%`, borderLeft: '1.5px dashed var(--theme-muted-text)' }}
          >
            <span
              className="absolute font-bold"
              style={{ top: 0, left: 4, fontSize: 9.5, color: 'var(--theme-muted-text)' }}
            >
              hoje
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

DraftMiniGantt.displayName = 'DraftMiniGantt';
